import { useDndMonitor } from '@dnd-kit/core';
import { useEffect, useRef, useState, type RefObject } from 'react';
import { range } from './derive';
import { useDropPreview } from './dropPreview';

/**
 * The name being dragged, beside the pointer, with what dropping it here would
 * do. It follows the pointer dnd-kit itself reads rather than the drag's delta,
 * which the board's auto-scroll shifts, and moves by style so a drag renders
 * nothing per pixel.
 */
export function DragChip({ pointer }: { pointer: RefObject<{ x: number; y: number } | null> }) {
  const [name, setName] = useState<string | null>(null);
  const preview = useDropPreview((s) => s.preview);
  const ref = useRef<HTMLDivElement>(null);
  useDndMonitor({
    onDragStart: (e) => setName((e.active.data.current?.name as string | undefined) ?? null),
    onDragEnd: () => setName(null),
    onDragCancel: () => setName(null),
  });
  useEffect(() => {
    if (!name) return;
    let frame = 0;
    const follow = () => {
      const p = pointer.current;
      if (p && ref.current) ref.current.style.transform = `translate(${p.x + 14}px, ${p.y + 16}px)`;
      frame = requestAnimationFrame(follow);
    };
    frame = requestAnimationFrame(follow);
    return () => cancelAnimationFrame(frame);
  }, [name, pointer]);
  if (!name) return null;
  return (
    <div ref={ref} className={`tl-dragchip${preview ? ` tl-dragchip--${preview.tone}` : ''}`} aria-hidden="true">
      {name}
      {preview ? (
        <small>
          {preview.postCode} {range(preview.fromMin, preview.toMin)} · {preview.label}
        </small>
      ) : null}
    </div>
  );
}
